import { DOMAINS } from "@repo/common";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import { webPaths } from "@repo/common/src/utils/screenPathCreator";
import {
  getMetadata,
  getStorage,
  list,
  listAll,
  ref,
  uploadBytes,
  deleteObject,
  type FirebaseStorage,
  type StorageReference,
} from "firebase/storage";

const FOLDER_PLACEHOLDER_FILENAME = ".keep";

export interface WebDownloadFileEntry {
  name: string;
  relativePath: string;
  sizeBytes: number;
  updatedAt: Date;
}

export interface WebDownloadFolderEntry {
  name: string;
  relativePath: string;
}

export interface WebDownloadsDirectoryListing {
  currentPath: string;
  folders: WebDownloadFolderEntry[];
  files: WebDownloadFileEntry[];
}

const normalizeRelativePath = (relativePath: string): string => relativePath.replace(/^\/+|\/+$/g, "");

const resolveStorage = (storage?: FirebaseStorage): FirebaseStorage => storage ?? getStorage();

const toFullStoragePath = (relativePath: string): string => {
  const normalized = normalizeRelativePath(relativePath);
  return normalized ? `${StoragePaths.WebDownloads}/${normalized}` : StoragePaths.WebDownloads;
};

const toStorageReference = (relativePath: string, storage?: FirebaseStorage): StorageReference =>
  ref(resolveStorage(storage), toFullStoragePath(relativePath));

const getNameFromStoragePath = (fullPath: string): string => {
  const segments = fullPath.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? "";
};

const getChildNameFromPrefix = (prefix: string): string => {
  const trimmed = prefix.replace(/\/$/, "");
  return getNameFromStoragePath(trimmed);
};

export const buildPublicWebDownloadUrl = (relativePath: string): string => {
  const publicPath = webPaths.getPublicWebDownloadPath(relativePath);
  return `${DOMAINS.WEBSITE}${publicPath}`;
};

export const listWebDownloadsDirectory = async (
  relativePath: string,
  storage?: FirebaseStorage,
): Promise<WebDownloadsDirectoryListing> => {
  const currentPath = normalizeRelativePath(relativePath);
  const listResult = await list(toStorageReference(currentPath, storage));

  const folders: WebDownloadFolderEntry[] = listResult.prefixes
    .map((prefixRef) => prefixRef.fullPath)
    .map((fullPrefixPath) => {
      const name = getChildNameFromPrefix(fullPrefixPath);
      const folderRelativePath = currentPath ? `${currentPath}/${name}` : name;
      return { name, relativePath: folderRelativePath };
    })
    .sort((left, right) => left.name.localeCompare(right.name, "de"));

  const files: WebDownloadFileEntry[] = (
    await Promise.all(
      listResult.items.map(async (item) => {
        const name = getNameFromStoragePath(item.fullPath);
        if (name === FOLDER_PLACEHOLDER_FILENAME) {
          return null;
        }

        const metadata = await getMetadata(item);
        const storagePrefix = `${StoragePaths.WebDownloads}/`;
        const relativeFilePath = item.fullPath.startsWith(storagePrefix)
          ? item.fullPath.slice(storagePrefix.length)
          : item.fullPath;

        return {
          name,
          relativePath: relativeFilePath,
          sizeBytes: metadata.size,
          updatedAt: new Date(metadata.updated),
        };
      }),
    )
  )
    .filter((entry): entry is WebDownloadFileEntry => entry !== null)
    .sort((left, right) => left.name.localeCompare(right.name, "de"));

  return { currentPath, folders, files };
};

export const uploadWebDownloadFile = async (
  relativeDirectoryPath: string,
  file: File,
  storage?: FirebaseStorage,
): Promise<string> => {
  const directoryPath = normalizeRelativePath(relativeDirectoryPath);
  const relativeFilePath = directoryPath ? `${directoryPath}/${file.name}` : file.name;
  const fileRef = toStorageReference(relativeFilePath, storage);
  await uploadBytes(fileRef, file, { contentType: file.type || "application/octet-stream" });
  return relativeFilePath;
};

export const createWebDownloadsFolder = async (
  parentRelativePath: string,
  folderName: string,
  storage?: FirebaseStorage,
): Promise<string> => {
  const parentPath = normalizeRelativePath(parentRelativePath);
  const folderRelativePath = parentPath ? `${parentPath}/${folderName}` : folderName;
  const placeholderRef = toStorageReference(`${folderRelativePath}/${FOLDER_PLACEHOLDER_FILENAME}`, storage);
  await uploadBytes(placeholderRef, new Uint8Array(), { contentType: "text/plain" });
  return folderRelativePath;
};

export const deleteWebDownloadFile = async (relativePath: string, storage?: FirebaseStorage): Promise<void> => {
  await deleteObject(toStorageReference(relativePath, storage));
};

const deleteStorageFolderContents = async (folderRef: StorageReference): Promise<void> => {
  const listResult = await listAll(folderRef);

  await Promise.all([
    ...listResult.items.map((item) => deleteObject(item)),
    ...listResult.prefixes.map((prefixRef) => deleteStorageFolderContents(prefixRef)),
  ]);
};

export const deleteWebDownloadsFolder = async (
  relativeFolderPath: string,
  storage?: FirebaseStorage,
): Promise<void> => {
  await deleteStorageFolderContents(toStorageReference(relativeFolderPath, storage));
};
